import React, { useState } from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { setError } from "../reducers/appSlice";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiTextField from "@mui/material/TextField";
import MuiFormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import TableFooter from "@mui/material/TableFooter";

import SearchIcon from "@mui/icons-material/Search";
import CachedIcon from "@mui/icons-material/Cached";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import DoneOutlineRounded from "@mui/icons-material/DoneOutlineRounded";

import Modal from "../components/Modal";
import api from "../service/api";

const FormControl = styled(MuiFormControl)(({ theme }) => ({
  margin: theme.spacing(1),
  minWidth: 200,
}));

const TextField = styled(MuiTextField)(({ theme }) => ({
  margin: theme.spacing(1),
}));

const ModalButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const validationSchema = Yup.object({
  descripcion: Yup.string().min(3, "Minimo 3 caracteres"),
});

const TablePaginationActions = (props) => {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const BuscadorTema = (props) => {
  const [datos, setDatos] = useState([]);
  const [open, setOpen] = React.useState(false);
  const [submitting, setSubmitting] = useState(false);

  const dispatch = useDispatch();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    formik.handleReset();
    setOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formik = useFormik({
    initialValues: {
      descripcion: "",
      general: "T",
      digital: "T",
      activo: props.activo,
      alta: props.alta,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setDatos([]);
      setPage(0);
      setSubmitting(true);
      api
        .buscarTema(values)
        .then((res) => {
          setDatos(res.data);
        })
        .catch((e) => {
          dispatch(setError(e.response.data.error));
          console.log("Error al cargar los datos");
        })
        .finally(() => setSubmitting(false));
    },
    onReset: () => {
      setDatos([]);
    },
  });

  const columns = [
    {
      id: "id",
      label: "Código",
      minWidth: 100,
      align: "right",
    },
    {
      id: "descTemaCompleto",
      label: "Tema",
      minWidth: 250,
      align: "left",
    },
    {
      id: "digital",
      label: "Soporte",
      minWidth: 150,
      align: "left",
    },
    {
      id: "general",
      label: "Alcance",
      minWidth: 100,
      align: "left",
    },
    {
      id: "activo",
      label: "Activo",
      minWidth: 100,
      align: "left",
    },
    {
      id: "acciones",
      label: "Acciones",
      minWidth: 50,
      align: "center",
    },
  ];

  if (props.activo === "S" || props.activo === "N") {
    columns.splice(4, 1);
  }

  return (
    <div>
      {props.mostrar === "boton" ? (
        <ModalButton
          variant="verde"
          onClick={handleOpen}
          endIcon={<SearchIcon />}
        >
          Temas
        </ModalButton>
      ) : (
        <IconButton color="primary" onClick={handleOpen}>
          <SearchIcon fontSize="small" />
        </IconButton>
      )}
      <Modal
        open={open}
        handleClose={handleClose}
        title="BUSCADOR DE TEMAS"
        responsiveTitle="TEMAS"
        closeButton
      >
        <form
          id="buscador"
          name="buscador"
          onSubmit={formik.handleSubmit}
          onReset={formik.handleReset}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                id="descripcion"
                name="descripcion"
                label="Tema"
                variant="outlined"
                size="small"
                margin="dense"
                fullWidth
                value={formik.values.descripcion}
                onChange={formik.handleChange}
                error={
                  formik.touched.descripcion &&
                  Boolean(formik.errors.descripcion)
                }
                helperText={
                  formik.touched.descripcion && formik.errors.descripcion
                }
              />
            </Grid>
            {/* <Grid item xs={12} sm={6}>
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
                error={formik.touched.general && Boolean(formik.errors.general)}
              >
                <InputLabel id="generalLabel">Alcance</InputLabel>
                <Select
                  labelId="generalLabel"
                  id="general"
                  name="general"
                  variant="outlined"
                  label="Alcance"
                  value={formik.values.general}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="T">TODOS</MenuItem>
                  <MenuItem value="S">GENERAL</MenuItem>
                  <MenuItem value="N">PARTICULAR</MenuItem>
                </Select>
                <FormHelperText>
                  {formik.touched.general && formik.errors.general}
                </FormHelperText>
              </FormControl>
            </Grid> */}
            <Grid item xs={12} sm={6}>
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
                error={formik.touched.digital && Boolean(formik.errors.digital)}
              >
                <InputLabel id="digitalLabel">Soporte</InputLabel>
                <Select
                  labelId="digitalLabel"
                  id="digital"
                  name="digital"
                  variant="outlined"
                  label="Soporte"
                  value={formik.values.digital}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="T">TODOS</MenuItem>
                  <MenuItem value="S">DIGITAL</MenuItem>
                  <MenuItem value="N">PAPEL</MenuItem>
                </Select>
                <FormHelperText>
                  {formik.touched.digital && formik.errors.digital}
                </FormHelperText>
              </FormControl>
            </Grid>

            {props.activo === "S" || props.activo === "N" ? (
              null
            ) : (
              <Grid item xs={12} sm={6}>
                <FormControl
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={formik.touched.activo && Boolean(formik.errors.activo)}
                >
                  <InputLabel id="activoLabel">Activo</InputLabel>
                  <Select
                    labelId="activoLabel"
                    id="activo"
                    name="activo"
                    variant="outlined"
                    label="Activo"
                    value={formik.values.activo}
                    onChange={formik.handleChange}
                  >
                    <MenuItem value="T">TODOS</MenuItem>
                    <MenuItem value="S">ACTIVOS</MenuItem>
                    <MenuItem value="N">INACTIVOS</MenuItem>
                  </Select>
                  <FormHelperText>
                    {formik.touched.activo && formik.errors.activo}
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}

            <Grid
              item
              xs={12}
              align="right"
              sx={{ display: { xs: "none", md: "inline" } }}
            >
              <Button
                variant="plata"
                type="reset"
                endIcon={<CachedIcon />}
                sx={{ m: 1 }}
              >
                {"Limpiar"}
              </Button>{" "}
              <Button
                sx={{ m: 1 }}
                variant="verde"
                color="primary"
                type="submit"
                disabled={submitting}
                endIcon={<SearchIcon />}
              >
                {submitting ? "Buscando..." : "Buscar"}
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              align="right"
              sx={{ display: { xs: "block", md: "none" } }}
            >
              <IconButton color="primary" type="reset" title="Limpiar">
                <CachedIcon />
              </IconButton>{" "}
              <IconButton
                color="primary"
                type="submit"
                disabled={submitting}
                title="Buscar"
              >
                <SearchIcon />
              </IconButton>
            </Grid>

            <Grid item xs={12} sm={12}>
              {" "}
            </Grid>
          </Grid>
        </form>

        <TableContainer sx={{ maxHeight: 440 }}>
          <Table size="small" sx={{ border: "1px solid #dee2e6" }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    sx={{
                      backgroundColor: "#fff",
                      color: "#3c4b64",
                      minWidth: column.minWidth,
                    }}
                    key={column.id}
                    align={column.align}
                  >
                    <b>{column.label}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {datos
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((dato, index) => {
                  return (
                    <TableRow hover tabIndex={-1} key={index}>
                      {columns.map((column) => {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {column.id === "acciones" ? (
                              <IconButton
                                title="Seleccionar"
                                onClick={() => {
                                  props.resultado(dato);
                                  handleClose();
                                }}
                              >
                                <DoneOutlineRounded sx={{ fontSize: 14 }} />
                              </IconButton>
                            ) : (
                              dato[column.id]
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                {submitting ? (
                  <TableRow>
                    <TableCell colSpan="7" align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  null
                )}
                {datos.length === 0 && !submitting ? (
                  <TableRow>
                  <TableCell colSpan="7" align="center">
                    <Typography variant="aclaracion">No hay datos para mostrar</Typography>
                  </TableCell>
                </TableRow>
                ) : (
                  null
                )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[
                    5,
                    10,
                    25,
                    { label: "Todas", value: datos.length },
                  ]}
                  colSpan={columns.length}
                  count={datos.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      "aria-label": "filas pr página",
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Modal>
    </div>
  );
};

export default BuscadorTema;
